import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { getHomeSnapshot } from '../../shell/home/homeSnapshotRepo';
import {
  getStoreActivationSnapshot,
  type StoreActivationRemoteSnapshot,
} from './storeActivationSnapshotRepo';
import {
  isOnlineOperatingDismissed,
  isStoreActivationDismissed,
  isWeekOneEssentialsSaved,
  isWeekOneMpSkipped,
  isWeekOnePrinterSkipped,
  isWeekOneSetupDismissed,
  isWeekOneTeamSkipped,
} from './onboardingUiStorage';
// Import directo al repo (no public-api): evita tirar el barrel completo
// de printing (que arrastra pos) en el path de onboarding.
import { getBranchReceiptPrintConfig } from '../../printing/infra/printConfigRepo';

export type { StoreActivationFlags } from './storeActivationGating';
import type { StoreActivationFlags } from './storeActivationGating';

export type StoreActivationEvidence = {
  /** Flags locales + home snapshot resueltos. */
  loaded: boolean;
  loading: boolean;
  /** storefront.is_public del home snapshot (null = desconocido). */
  isPublic: boolean | null;
  /** Ventas lifetime de la branch (home snapshot; null si la sección falló). */
  lifetimeSales: number | null;
  /** Evidencia remota de activación; null cuando ninguna card la necesita o falló. */
  activation: StoreActivationRemoteSnapshot | null;
  flags: StoreActivationFlags | null;
  reload: (opts?: { force?: boolean }) => Promise<void>;
};

const DEFAULT_FLAGS: StoreActivationFlags = {
  storeDismissed: true,
  onlineDismissed: true,
  weekOneDismissed: true,
  teamSkipped: false,
  mpSkipped: false,
  essentialsSaved: false,
  printerSkipped: false,
  printerReady: false,
};

async function loadFlags(userId: string, branchId: string): Promise<StoreActivationFlags> {
  const [
    storeDismissed,
    onlineDismissed,
    weekOneDismissed,
    teamSkipped,
    mpSkipped,
    essentialsSaved,
    printerSkipped,
    printConfig,
  ] = await Promise.all([
    isStoreActivationDismissed(userId).catch(() => true),
    isOnlineOperatingDismissed(userId).catch(() => true),
    isWeekOneSetupDismissed(userId).catch(() => true),
    isWeekOneTeamSkipped(userId).catch(() => false),
    isWeekOneMpSkipped(userId).catch(() => false),
    isWeekOneEssentialsSaved(userId, branchId).catch(() => false),
    isWeekOnePrinterSkipped(userId).catch(() => false),
    getBranchReceiptPrintConfig(branchId).catch(() => null),
  ]);
  return {
    storeDismissed,
    onlineDismissed,
    weekOneDismissed,
    teamSkipped,
    mpSkipped,
    essentialsSaved,
    printerSkipped,
    printerReady: printConfig?.mode === 'agent',
  };
}

export { shouldLoadStoreActivationEvidence } from './storeActivationGating';
import {
  resolveOnlineSetupComplete,
  shouldLoadStoreActivationEvidence,
} from './storeActivationGating';

/**
 * Una sola evidencia para las 3 cards de onboarding de Inicio.
 *
 * Antes cada card fetcheaba por su lado (profile + zonas + MP + counts) solo
 * para decidir si renderizarse — ~15 requests por mount incluso con la tienda
 * ya publicada. Ahora: flags locales (gratis) + home_snapshot (compartido con
 * el resto de Inicio) deciden si hace falta `store_activation_snapshot`:
 *
 * - `!is_public` y checklist no dismissido → sí (la card se muestra).
 * - `is_public` y setup-online no dismissido e incompleto → sí. Si
 *   `resolveOnlineSetupComplete` dice completo, la card nunca renderiza y el
 *   bundle no hace falta (hechos en storefront: online_orders_count +
 *   delivery_ready).
 * - semana-1 con ventas, no dismissida y con pasos que necesitan evidencia
 *   remota (empleados/invitaciones/MP) → sí.
 *
 * Si ninguna aplica, `activation` queda null y no hay llamada extra.
 */
export function useStoreActivationEvidence({
  userId,
  isOwner,
  offerAllowed,
}: {
  userId: string | undefined;
  isOwner: boolean;
  offerAllowed: boolean;
}): StoreActivationEvidence {
  const branchId = useSessionStore((s) => s.currentBranchId);
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const canBranch = useHasBusinessCapability('employees');
  const hasDelivery = useHasBusinessCapability('delivery_fulfillment');
  const { entitlements } = usePlanEntitlements();
  const mpPlanAllowed = entitlements?.mp_qr_pos === true;

  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPublic, setIsPublic] = useState<boolean | null>(null);
  const [lifetimeSales, setLifetimeSales] = useState<number | null>(null);
  const [activation, setActivation] = useState<StoreActivationRemoteSnapshot | null>(null);
  const [flags, setFlags] = useState<StoreActivationFlags | null>(null);
  const generationRef = useRef(0);

  const baseEligible = Boolean(
    userId && isOwner && offerAllowed && canBranch && publicStoreOn && branchId,
  );

  const reload = useCallback(
    async (opts?: { force?: boolean }) => {
      const generation = ++generationRef.current;
      const stale = () => generation !== generationRef.current;
      if (!userId || !branchId) {
        if (!stale()) {
          setFlags(userId ? DEFAULT_FLAGS : null);
          setIsPublic(null);
          setLifetimeSales(null);
          setActivation(null);
          setLoaded(true);
        }
        return;
      }
      setLoading(true);
      try {
        const [f, snap] = await Promise.all([
          loadFlags(userId, branchId),
          getHomeSnapshot(branchId, { force: opts?.force }).catch(() => null),
        ]);
        if (stale()) return;
        setFlags(f);
        const pub = snap?.storefront?.isPublic ?? null;
        const sales = snap?.lifetimeSales ?? null;
        setIsPublic(pub);
        setLifetimeSales(sales);
        setLoaded(true);

        const needsActivation = shouldLoadStoreActivationEvidence({
          baseEligible,
          snapshotLoaded: snap != null,
          isPublic: pub,
          lifetimeSales: sales,
          onlineSetupComplete: resolveOnlineSetupComplete({
            isPublic: pub,
            deliveryReady: snap?.storefront?.deliveryReady ?? null,
            hasDelivery,
          }),
          flags: f,
          mpPlanAllowed,
        });

        if (needsActivation) {
          const act = await getStoreActivationSnapshot(branchId, {
            force: opts?.force,
          }).catch(() => null);
          if (!stale()) setActivation(act);
        } else if (!stale()) {
          setActivation(null);
        }
      } finally {
        if (!stale()) setLoading(false);
      }
    },
    [baseEligible, branchId, hasDelivery, mpPlanAllowed, userId],
  );

  useEffect(() => {
    setLoaded(false);
    void reload();
  }, [reload]);

  return { loaded, loading, isPublic, lifetimeSales, activation, flags, reload };
}
